import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Lock, RefreshCw, Unlock } from "lucide-react";

import { adminApi, type ClosedPeriod } from "@/api/admin";
import { useMe } from "@/hooks/useMe";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

function thisMonthIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtMonth(s: string): string {
  return s.slice(0, 7);
}

function fmtDateTime(s: string): string {
  return new Date(s).toLocaleString("uk-UA", { timeZone: "Europe/Kyiv" });
}

export function PeriodsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const qc = useQueryClient();
  const [month, setMonth] = useState(thisMonthIso());
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isSuperAdmin = me.data?.role === "super_admin";

  const periods = useQuery({
    queryKey: ["closed-periods"],
    queryFn: () => adminApi.listClosedPeriods(),
  });

  const sorted = useMemo(
    () =>
      [...(periods.data ?? [])].sort((a, b) =>
        b.period_start_month.localeCompare(a.period_start_month),
      ),
    [periods.data],
  );

  const close = useMutation({
    mutationFn: () => adminApi.closePeriod(month, reason || undefined),
    onSuccess: () => {
      setError(null);
      setReason("");
      void qc.invalidateQueries({ queryKey: ["closed-periods"] });
    },
    onError: (e: unknown) => {
      const code = (e as AxiosError<{ error?: { code?: string } }>)?.response?.data?.error?.code;
      setError(code === "period_already_closed" ? t("plan.errors.period_closed") : t("clients.errors.unknown"));
    },
  });

  const open = useMutation({
    mutationFn: (p: ClosedPeriod) =>
      adminApi.openPeriod(fmtMonth(p.period_start_month), reason || "(no reason)"),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["closed-periods"] });
    },
    onError: () => setError(t("clients.errors.unknown")),
  });

  const onClose = () => {
    if (!confirm(t("periods.close_confirm", { month }))) return;
    close.mutate();
  };

  const onOpen = (p: ClosedPeriod) => {
    if (!confirm(t("periods.reopen_confirm", { month: fmtMonth(p.period_start_month) }))) return;
    open.mutate(p);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("periods.title")}</CardTitle>
          <CardDescription>{t("periods.subtitle")}</CardDescription>
        </CardHeader>
        {isSuperAdmin && (
          <CardContent className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label>{t("periods.month")}</Label>
              <Input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-[180px]"
              />
            </div>
            <div className="space-y-1 flex-1 min-w-[200px]">
              <Label>{t("periods.actions")}</Label>
              <Input
                placeholder="reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <Button onClick={onClose} disabled={close.isPending}>
              <Lock className="h-4 w-4" /> {t("periods.close_btn")}
            </Button>
          </CardContent>
        )}
        {error && (
          <CardContent className="pt-0">
            <div className="text-sm text-red-600">{error}</div>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardContent className="p-0">
          {periods.isLoading ? (
            <div className="p-6 text-sm text-slate-500">{t("common.loading")}</div>
          ) : sorted.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">{t("periods.no_closed")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-500">
                    <th className="p-2">{t("periods.month")}</th>
                    <th className="p-2">{t("periods.closed_at")}</th>
                    <th className="p-2">{t("common.active")}</th>
                    <th className="p-2">reason</th>
                    {isSuperAdmin && <th className="p-2 text-right">{t("periods.actions")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((p) => (
                    <tr key={p.period_start_month} className="border-t border-slate-100">
                      <td className="p-2 font-mono">{fmtMonth(p.period_start_month)}</td>
                      <td className="p-2 text-xs text-slate-500">{fmtDateTime(p.closed_at)}</td>
                      <td className="p-2">{p.is_manual ? "manual" : "auto"}</td>
                      <td className="p-2 text-xs text-slate-500">{p.reason ?? "—"}</td>
                      {isSuperAdmin && (
                        <td className="p-2 text-right">
                          <Button size="sm" variant="outline" onClick={() => onOpen(p)} disabled={open.isPending}>
                            <Unlock className="h-4 w-4" /> {t("periods.reopen_btn")}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {isSuperAdmin && <RecalcCard />}
    </div>
  );
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function RecalcCard() {
  const { t } = useTranslation();
  const today = todayIso();
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recalc = useMutation({
    mutationFn: () => adminApi.recalc(from, to),
    onSuccess: (r: { ok: boolean; queued: boolean }) => {
      setResult(r.queued ? t("periods.recalc_queued") : t("periods.recalc_done"));
      setError(null);
    },
    onError: (e: unknown) => {
      const code = (e as AxiosError<{ error?: { code?: string; details?: { first_closed_month?: string } } }>)
        ?.response?.data?.error;
      if (code?.code === "period_closed") {
        setError(
          t("periods.recalc_error_period_closed", {
            month: code.details?.first_closed_month ?? "?",
          }),
        );
      } else {
        setError(t("clients.errors.unknown"));
      }
      setResult(null);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("periods.recalc_title")}</CardTitle>
        <CardDescription>{t("periods.recalc_subtitle")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label>{t("audit.from")}</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[180px]" />
        </div>
        <div className="space-y-1">
          <Label>{t("audit.to")}</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[180px]" />
        </div>
        <Button onClick={() => recalc.mutate()} disabled={recalc.isPending || from > to}>
          <RefreshCw className={recalc.isPending ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
          {t("periods.recalc_btn")}
        </Button>
        {result && <span className="text-sm text-emerald-700">{result}</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </CardContent>
    </Card>
  );
}
